import { warnLog } from '@/utils/log'
import { Breakpoint } from '@/utils/types'
import { useEffect, useState } from 'react'

const BREAKPOINTS: Record<Breakpoint, { min: number; max: number | null }> = {
  small: { min: 320, max: 767 },
  medium: { min: 768, max: 1023 },
  large: { min: 1024, max: 1440 },
  xlarge: { min: 1440, max: null },
}

function getBreakpoint(width: number): Breakpoint {
  if (width >= BREAKPOINTS.xlarge.min) return 'xlarge'
  if (width >= BREAKPOINTS.large.min && width <= BREAKPOINTS.large.max!)
    return 'large'
  if (width >= BREAKPOINTS.medium.min && width <= BREAKPOINTS.medium.max!)
    return 'medium'
  return 'small'
}

export interface UseBreakpointReturn {
  breakpoint: Breakpoint
  isMobile?: boolean
  isTablet?: boolean
  isDesktop?: boolean
}

/**
 * React hook to determine the current breakpoint based on window width
 * - Aggiornamento su resize con debounce
 * - Ritorna il breakpoint corrente
 */
export const useBreakpoint = (): UseBreakpointReturn => {
  const getWidth = () => {
    const isWindowDefined = typeof window !== 'undefined'
    if (isWindowDefined) {
      return window.innerWidth
    } else {
      warnLog(
        '[useBreakpoint] - window is not defined, returning default breakpoint "medium"'
      )
      return BREAKPOINTS.medium.min
    }
  }
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(
    getBreakpoint(getWidth())
  )

  useEffect(() => {
    const isWindowDefined = typeof window !== 'undefined'
    if (!isWindowDefined) return

    let timeout: number | undefined

    const handleResize = () => {
      clearTimeout(timeout)
      timeout = window.setTimeout(() => {
        const newBreakpoint = getBreakpoint(window.innerWidth)
        setBreakpoint(newBreakpoint)
      }, 100) // Debounce di 100ms
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(timeout)
    }
  }, [])

  return {
    breakpoint,
    isMobile: breakpoint === 'small',
    isTablet: breakpoint === 'medium',
    isDesktop: breakpoint === 'large' || breakpoint === 'xlarge',
  }
}
